import React from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextFieldFormik from '~/components/TextFieldFormik';
import type {
  FundAccountEngineConfigInfo,
  UpdateFundAccountEngineConfigParams,
} from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import { updateFundAccountEngineConfig } from '../api/fundAccountRulesApi';
import { TEXTS } from '../texts';

import type { DefaultBehavior, DefaultBehaviorFormValues } from './defaultBehavior';
import { BEHAVIOR_OPTIONS, BEHAVIOR_VALUES, DEFAULT_BEHAVIOR, FIELD_IDS, initialFormValues } from './defaultBehavior';

const validationSchema = Yup.object().shape({
  [FIELD_IDS.DEFAULT_BEHAVIOR]: Yup.string()
    .oneOf(BEHAVIOR_VALUES, TEXTS.BEHAVIOR_REQUIRED)
    .required(TEXTS.BEHAVIOR_REQUIRED),
  // `.trim()` casts a whitespace-only value to '' before `.required()` runs, mirroring the BE gate
  // which normalizes a blank reference to null *before* checking it — a bare `.required()` would
  // let '   ' through the form and come back as a 400 the admin cannot read off the field.
  [FIELD_IDS.DEFAULT_ACCOUNT_ID]: Yup.string().when(FIELD_IDS.DEFAULT_BEHAVIOR, {
    is: DEFAULT_BEHAVIOR.DefaultAccount,
    then: (schema) => schema.trim().required(TEXTS.ACCOUNT_REQUIRED),
    otherwise: (schema) => schema,
  }),
});

interface EditDefaultBehaviorDialogProps {
  organizationId: number;
  config: FundAccountEngineConfigInfo;
  onSaved: () => Promise<void> | void;
  onClose: () => void;
}

const EditDefaultBehaviorDialog: React.FC<EditDefaultBehaviorDialogProps> = ({
  organizationId,
  config,
  onSaved,
  onClose,
}) => (
  <Formik<DefaultBehaviorFormValues>
    initialValues={initialFormValues(config)}
    validationSchema={validationSchema}
    onSubmit={async (values) => {
      try {
        // The PUT replaces both fields wholesale, so a behavior that carries no account must send
        // an explicit null rather than leave the previously stored one in place.
        const params: UpdateFundAccountEngineConfigParams = {
          default_behavior: values.default_behavior as DefaultBehavior,
          default_account_id:
            values.default_behavior === DEFAULT_BEHAVIOR.DefaultAccount ? values.default_account_id.trim() : null,
        };
        await updateFundAccountEngineConfig(organizationId, params);
        await onSaved();
        onClose();
      } catch {
        // Swallowed rather than rethrown, and with no `setSubmitting(false)`: the api layer already
        // surfaced the failure via `reportAxiosError`, Formik clears the flag itself once the
        // returned promise settles (its reducer sets `isSubmitting: false` on both SUBMIT_SUCCESS
        // and SUBMIT_FAILURE), and letting the rejection escape makes Formik's own `handleSubmit`
        // log "An unhandled error was caught from submitForm()" on top of the toast.
      }
    }}
  >
    <EditDefaultBehaviorDialogInner onClose={onClose} />
  </Formik>
);

interface EditDefaultBehaviorDialogInnerProps {
  onClose: () => void;
}

const EditDefaultBehaviorDialogInner: React.FC<EditDefaultBehaviorDialogInnerProps> = ({ onClose }) => {
  const { values, isSubmitting, handleSubmit } = useFormikContext<DefaultBehaviorFormValues>();
  const { classes } = useStyles();

  return (
    <CardDialog
      isDialog
      title={TEXTS.EDIT_DIALOG_TITLE}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions onClickPrimary={() => handleSubmit()} onClickSecondary={onClose} disabled={isSubmitting} />
      }
    >
      <TextFieldFormik
        id={FIELD_IDS.DEFAULT_BEHAVIOR}
        label={TEXTS.BEHAVIOR_FIELD_LABEL}
        // `mt-12`, not decoration: an outlined field floats its label above the input box, and this
        // one sits flush against the top of the dialog's `overflow-y: auto` content, so without the
        // offset the label is clipped.
        className={cn(classes.textField, 'mt-12')}
        fullWidth
        variant="outlined"
        select
        disabled={isSubmitting}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error -- TextFieldFormik is a JS component; its children prop infers as null|undefined */}
        {BEHAVIOR_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextFieldFormik>
      {values.default_behavior === DEFAULT_BEHAVIOR.DefaultAccount ? (
        <TextFieldFormik
          id={FIELD_IDS.DEFAULT_ACCOUNT_ID}
          label={TEXTS.ACCOUNT_FIELD_LABEL}
          className={cn(classes.textField, 'mt-12')}
          fullWidth
          variant="outlined"
          disabled={isSubmitting}
        />
      ) : null}
      {values.default_behavior === DEFAULT_BEHAVIOR.SendEmpty ? (
        <AlertBanner
          className="mt-12"
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          title={TEXTS.ALERT_TITLE}
          note={TEXTS.SEND_EMPTY_WARNING}
          withIcon
        />
      ) : null}
    </CardDialog>
  );
};

export default EditDefaultBehaviorDialog;
